import graphql from "babel-plugin-relay/macro";
import { differenceInMinutes, getMinutes, parse } from "date-fns";
import { ReactNode } from "react";
import { useFragment } from "react-relay";

import { useFlags } from "@/utils/launchDarkly";
import { isBlockedOrUnavailableSlotAvailability } from "@/utils/slotAvailability";
import {
  AGENDA_MINUTES_PER_SLOT,
  COLUMN_MIN_WIDTH_PX,
} from "@whoosh/design-system/src/apps/PatronApp/screens/multiSlotBooking";
import { Box, VStack } from "@whoosh/design-system/src/components/primitive";
import {
  convertAgendaDateTimeToDate,
  formatAgendaDateTimeHHMM,
  formatHHMM,
  getHoursMinutesFromHHMM,
} from "@whoosh/utilities/src/dates";

import { getFacilityMemberPermissions } from "../../../../../../../hooks/getFacilityMemberPermissions";
import {
  DEFAULT_SLOT_DURATION_MINUTES,
  MIN_SLOT_DURATION_MINUTES,
} from "../../../MultiSlotBookingScreenRenderer";
import { BlockSlot } from "./BlockSlot";
import { BookingSlot } from "./BookingSlot";
import { BufferSlot } from "./BufferSlot";
import { EmptySlot } from "./EmptySlot";
import { EventSlot } from "./EventSlot/EventSlot";
import { RequestRestrictionSlot } from "./RequestRestrictionSlot";
import { UnavailableSlot } from "./UnavailableSlot";
import { SingleCourseColumn_course$key } from "./__generated__/SingleCourseColumn_course.graphql";

type courseColumnItems = {
  nextSlotToRender: any;
  slots: ReactNode[];
};

type RequestRestrictionState =
  | "blocking"
  | "bookable"
  | "published"
  | "request_open";

type RequestRestrictionEdge = {
  liftAt?: string | null;
  requestOpens?: string | null;
  requestCloses?: string | null;
  node?: {
    id?: string;
    requestConfig?: { id?: string | null } | null;
    memberVisibility?:
      | "NONE"
      | "RESTRICTION_ONLY"
      | "SLOTS_AND_RESTRICTION"
      | "SLOTS_AND_RESTRICTION_INLINE"
      | "SLOTS_ONLY"
      | null;
  } | null;
} | null;

const getRequestRestrictionState = (
  restrictionEdge: RequestRestrictionEdge | null | undefined,
  nowMs: number
): RequestRestrictionState => {
  if (restrictionEdge?.liftAt) {
    return "published";
  }

  const requestOpensMs = restrictionEdge?.requestOpens
    ? Date.parse(restrictionEdge.requestOpens)
    : null;
  const requestClosesMs = restrictionEdge?.requestCloses
    ? Date.parse(restrictionEdge.requestCloses)
    : null;
  const hasOpened =
    requestOpensMs === null ||
    Number.isNaN(requestOpensMs) ||
    requestOpensMs <= nowMs;
  const hasClosed =
    requestClosesMs !== null &&
    !Number.isNaN(requestClosesMs) &&
    requestClosesMs < nowMs;

  const isDefaultRequestRule = !restrictionEdge?.node?.requestConfig;

  if (!hasOpened) {
    return "blocking";
  }

  if (!hasClosed) {
    return "request_open";
  }

  return isDefaultRequestRule ? "bookable" : "blocking";
};

export const getRequestRestrictionDisplayState = ({
  currentRequestRestrictionEdges,
  nextRequestRestrictionEdges,
  requestConfigs,
  nowMs,
}: {
  currentRequestRestrictionEdges?:
    | readonly (RequestRestrictionEdge | null | undefined)[]
    | null;
  nextRequestRestrictionEdges?:
    | readonly (RequestRestrictionEdge | null | undefined)[]
    | null;
  requestConfigs: boolean;
  nowMs: number;
}) => {
  const isRequestOpen = (
    restrictionEdge: RequestRestrictionEdge | null | undefined
  ) =>
    !requestConfigs ||
    getRequestRestrictionState(restrictionEdge, nowMs) === "request_open";

  const currentSlotHasRequestRestrictions =
    currentRequestRestrictionEdges?.some(isRequestOpen) ?? false;
  const nextSlotHasRequestRestrictions =
    nextRequestRestrictionEdges?.some(isRequestOpen) ?? false;
  const slotHasHiddenRequestRestrictions =
    requestConfigs &&
    (currentRequestRestrictionEdges?.some(
      (restrictionEdge) =>
        getRequestRestrictionState(restrictionEdge, nowMs) === "blocking" &&
        restrictionEdge?.node?.memberVisibility === "SLOTS_ONLY"
    ) ??
      false);

  return {
    currentSlotHasRequestRestrictions,
    nextSlotHasRequestRestrictions,
    slotHasHiddenRequestRestrictions,
    slotHasBlockingRequestRestrictions:
      requestConfigs &&
      (currentRequestRestrictionEdges?.some(
        (restrictionEdge) =>
          getRequestRestrictionState(restrictionEdge, nowMs) === "blocking"
      ) ??
        false),
    isRequestSlot:
      currentSlotHasRequestRestrictions &&
      (nextSlotHasRequestRestrictions || !nextRequestRestrictionEdges),
  };
};

type SingleCourseColumnProps = {
  courseRef: SingleCourseColumn_course$key;
  courseName: string;
  firstAndLastTime:
    | {
        firstTime: string | null;
        lastTime: string | null;
      }
    | undefined;
  currentSlotId?: string;
  queueSlotId?: string;
  handleMultiSlotRowPress?: (slotId: string) => void;
  setBookingWizardSlotId?: (slotId: string) => void;
  setBookingWizardStartTime?: (startTime: string) => void;
};

const isUnavailableColumnAvailability = (
  availability: string | null | undefined
): boolean =>
  isBlockedOrUnavailableSlotAvailability(availability) ||
  availability === "AVAILABLE_AFTER_START";

const getUnavailableTime = (
  localTime: string | null,
  agendaTime: string | null
) => {
  if (localTime === null || agendaTime === null) {
    return 0;
  }
  const localTimeObj = convertAgendaDateTimeToDate(localTime);

  const agendaTimeObj = convertAgendaDateTimeToDate(agendaTime);

  //Bump if first time is not on the hour as our time bar renders hours
  const minutesFromHour = getMinutes(agendaTimeObj);

  const differenceInMinutesBetweenLocalTimeAndAgendaTime =
    localTimeObj && agendaTimeObj
      ? differenceInMinutes(localTimeObj, agendaTimeObj) + minutesFromHour
      : 0;

  return Math.abs(differenceInMinutesBetweenLocalTimeAndAgendaTime);
};

export const sortCourseTimeSlotEdgesByDateTime = <
  T extends { node?: { dateTime?: string | null } | null | undefined }
>(
  edges: readonly (T | null | undefined)[]
): (T | null | undefined)[] =>
  [...edges].sort((a, b) => {
    const aDateTime = a?.node?.dateTime;
    const bDateTime = b?.node?.dateTime;

    if (!aDateTime && !bDateTime) {
      return 0;
    }

    if (!aDateTime) {
      return 1;
    }

    if (!bDateTime) {
      return -1;
    }

    return (
      convertAgendaDateTimeToDate(aDateTime).getTime() -
      convertAgendaDateTimeToDate(bDateTime).getTime()
    );
  });

export const SingleCourseColumn = ({
  courseRef,
  courseName,
  currentSlotId,
  queueSlotId,
  handleMultiSlotRowPress,
  firstAndLastTime,
  setBookingWizardSlotId,
  setBookingWizardStartTime,
}: SingleCourseColumnProps) => {
  const course = useFragment(SingleCourseColumnFragmentString, courseRef);
  const { requestConfigs } = useFlags();
  const nowMs = Date.now();

  const { memberAccess } = getFacilityMemberPermissions({
    fragmentRef: course.facility,
  });

  const timeSlotEdges = sortCourseTimeSlotEdgesByDateTime(
    course.timeSlots?.edges ?? []
  );

  const localFirstTime =
    timeSlotEdges.find((e) => e?.node?.dateTime)?.node?.dateTime ?? null;

  const localLastTime = timeSlotEdges.slice(-1)[0]?.node?.dateTime ?? null;

  const agendaFirstTime = firstAndLastTime?.firstTime ?? null;

  const agendaLastTime = firstAndLastTime?.lastTime ?? null;

  const topUnavailableMinutes = getUnavailableTime(
    localFirstTime,
    agendaFirstTime
  );

  const endUnavailableMinutes = getUnavailableTime(
    localLastTime,
    agendaLastTime
  );

  const canMemberBook = ["VISIBLE", "JOINABLE", "HIDDEN"].includes(
    memberAccess
  );

  const lastSlotIndex = course?.timeSlots?.edges?.length
    ? course?.timeSlots?.edges?.length - 1
    : 0;

  const slots = timeSlotEdges.reduce(
    (acc: courseColumnItems, cur, index) => {
      /**
       * If we have not set a next slot to render, then just return acc and move on
       * OR if the current item does not match the next one we want to render, continue on.
       * This is because we have to repeatedly check how many slots we're skipping
       */
      if (
        !acc.nextSlotToRender?.node?.id ||
        cur?.node?.id !== acc.nextSlotToRender?.node?.id
      ) {
        return acc;
      }

      const nextSlot = timeSlotEdges[index + 1]?.node ?? null;

      // Handle Slots that have Request Restrictions
      const currentRequestRestrictionEdges = requestConfigs
        ? cur?.node?.configuredRequestRestrictions?.edges
        : cur?.node?.requestRestrictions?.edges;
      const nextRequestRestrictionEdges = requestConfigs
        ? nextSlot?.configuredRequestRestrictions?.edges
        : nextSlot?.requestRestrictions?.edges;
      const {
        currentSlotHasRequestRestrictions,
        slotHasHiddenRequestRestrictions,
        slotHasBlockingRequestRestrictions,
        isRequestSlot,
      } = getRequestRestrictionDisplayState({
        currentRequestRestrictionEdges,
        nextRequestRestrictionEdges: nextSlot
          ? nextRequestRestrictionEdges
          : null,
        requestConfigs: !!requestConfigs,
        nowMs,
      });

      const needTopUnavailableComponent =
        index === 0 && topUnavailableMinutes > 0;

      const newEndUnavailableComponent =
        index === lastSlotIndex && endUnavailableMinutes;

      /**
       * SLOT HAS A BLOCK
       */
      if (
        cur?.node?.block?.id &&
        cur.node.block.blockSeries.isVisibleToMembers &&
        !currentSlotHasRequestRestrictions
      ) {
        const blockId = cur.node.block.id;
        const blockComponent = (
          <>
            {needTopUnavailableComponent ? (
              <UnavailableSlot durationMins={topUnavailableMinutes} />
            ) : null}
            <BlockSlot blockRef={cur.node.block} />
            {newEndUnavailableComponent ? (
              <UnavailableSlot durationMins={endUnavailableMinutes} />
            ) : null}
          </>
        );
        const slotsGoingForward = timeSlotEdges.slice(index + 1);

        const nextSlotToRender = slotsGoingForward?.find(
          (s) => s?.node?.block?.id !== blockId
        );

        return { nextSlotToRender, slots: [...acc.slots, blockComponent] };
      }

      /**
       * SLOT HAS AN EVENT BUFFER
       */
      if (
        cur?.node?.eventSlot?.id &&
        cur.node.eventSlot?.eventSession?.id &&
        cur.node.eventSlot?.type === "BUFFER"
      ) {
        const topBufferMins =
          cur.node.eventSlot?.eventSession?.bufferMinsBefore;
        const endBufferMins = cur.node.eventSlot?.eventSession?.bufferMinsAfter;

        const previousSlot = timeSlotEdges[index - 1]?.node ?? null;

        const isEndBufffer = previousSlot?.eventSlot?.type === "EVENT";

        const eventBufferComponent = (
          <>
            {needTopUnavailableComponent ? (
              <UnavailableSlot durationMins={topUnavailableMinutes} />
            ) : null}
            <UnavailableSlot
              durationMins={
                isEndBufffer ? endBufferMins ?? 15 : topBufferMins ?? 15
              }
            />
            {newEndUnavailableComponent ? (
              <UnavailableSlot durationMins={endUnavailableMinutes} />
            ) : null}
          </>
        );

        const slotsGoingForward = timeSlotEdges.slice(index + 1);
        const nextSlotToRender = slotsGoingForward?.find(
          (s) => s?.node?.eventSlot?.type !== "BUFFER"
        );
        return {
          nextSlotToRender,
          slots: [...acc.slots, eventBufferComponent],
        };
      }

      /**
       * SLOT HAS AN EVENT
       */
      if (
        cur?.node?.eventSlot?.id &&
        cur.node.eventSlot?.eventSession?.id &&
        cur.node.eventSlot?.type === "EVENT"
      ) {
        const eventId = cur.node.eventSlot?.eventSession?.id;
        const eventComponent = (
          <>
            {needTopUnavailableComponent ? (
              <UnavailableSlot durationMins={topUnavailableMinutes} />
            ) : null}
            <EventSlot eventRef={cur.node.eventSlot.eventSession} />
            {newEndUnavailableComponent ? (
              <UnavailableSlot durationMins={endUnavailableMinutes} />
            ) : null}
          </>
        );

        const slotsGoingForward = timeSlotEdges.slice(index + 1);
        const nextSlotToRender = slotsGoingForward?.find(
          (s) =>
            s?.node?.eventSlot?.eventSession?.id !== eventId ||
            s?.node?.eventSlot?.type === "BUFFER"
        );
        return { nextSlotToRender, slots: [...acc.slots, eventComponent] };
      }

      /**
       * SLOT HAS BOOKING
       */
      /**
       * @TODO: DOES NOT WORK WITH MULTI BOOKING SLOTS
       */
      const booking =
        cur?.node?.bookings?.edges?.find((e) => e?.node)?.node ?? null;
      if (booking?.id && cur?.node?.id) {
        const bufferSlotCount = booking?.bufferSlots?.count || 0;
        const bookingComponent = (
          <>
            {needTopUnavailableComponent ? (
              <UnavailableSlot durationMins={topUnavailableMinutes} />
            ) : null}
            <BookingSlot
              bookingRef={booking}
              slotId={cur.node.id}
              {...{ currentSlotId }}
              isRequestSlot={isRequestSlot}
            />
            {bufferSlotCount ? <BufferSlot count={bufferSlotCount} /> : null}
            {newEndUnavailableComponent ? (
              <UnavailableSlot durationMins={endUnavailableMinutes} />
            ) : null}
          </>
        );

        const bookingSlotIds = booking.slots?.edges
          ?.map((e) => e?.node?.id)
          .filter((item) => !!item);
        const slotCount = booking.slots?.count || 0;
        const slotsGoingForward = timeSlotEdges.slice(
          index + slotCount + bufferSlotCount
        );
        const nextSlotToRender = slotsGoingForward?.find(
          (s) => !bookingSlotIds?.includes(s?.node?.id)
        );
        return { nextSlotToRender, slots: [...acc.slots, bookingComponent] };
      }

      /**
       * SLOT HAS A SPLIT BOOKING
       */
      if (cur?.node?.hasSplitBooking) {
        const slotsGoingForward = timeSlotEdges.slice(index + 1);
        const nextSlotToRender = slotsGoingForward?.find(
          (s) =>
            !s?.node?.hasSplitBooking &&
            !isUnavailableColumnAvailability(s?.node?.availability)
        );
        const minsToNextSlot =
          nextSlotToRender?.node?.dateTime && cur?.node?.dateTime
            ? differenceInMinutes(
                convertAgendaDateTimeToDate(nextSlotToRender.node.dateTime),
                convertAgendaDateTimeToDate(cur.node.dateTime)
              )
            : 0;

        const unavailableComponent = (
          <>
            {needTopUnavailableComponent && newEndUnavailableComponent ? (
              <UnavailableSlot
                durationMins={
                  minsToNextSlot + topUnavailableMinutes + endUnavailableMinutes
                }
                startTime={
                  formatAgendaDateTimeHHMM(agendaFirstTime ?? "") ?? ""
                }
              />
            ) : needTopUnavailableComponent ? (
              <UnavailableSlot
                durationMins={minsToNextSlot + topUnavailableMinutes}
                startTime={
                  formatAgendaDateTimeHHMM(agendaFirstTime ?? "") ?? ""
                }
              />
            ) : newEndUnavailableComponent ? (
              <UnavailableSlot
                durationMins={minsToNextSlot + endUnavailableMinutes}
                startTime={
                  cur?.node?.dateTime
                    ? formatAgendaDateTimeHHMM(cur.node.dateTime) ?? ""
                    : formatHHMM(cur?.node.time)
                }
              />
            ) : (
              <UnavailableSlot
                durationMins={minsToNextSlot}
                startTime={
                  cur?.node?.dateTime
                    ? formatAgendaDateTimeHHMM(cur.node.dateTime) ?? ""
                    : formatHHMM(cur?.node.time)
                }
              />
            )}
          </>
        );
        return {
          nextSlotToRender,
          slots: [...acc.slots, unavailableComponent],
        };
      }

      /**
       * SLOT IS UNAVAILABLE/BLOCKED AND WE CANNOT SEE WHAT'S ON IT
       */
      /*
       * moving this before restrictions, so unavailable slots are shown on top of restrictions
       * so members cannot make a request in an already booked slot
       */
      if (
        isUnavailableColumnAvailability(cur?.node?.availability) &&
        !cur?.node?.block?.blockSeries.isVisibleToMembers &&
        !currentSlotHasRequestRestrictions
      ) {
        const slotsGoingForward = timeSlotEdges.slice(index + 1);
        // Find the next slot that is either available - or has something we can see to render on it.
        const nextSlotToRender =
          slotsGoingForward?.find(
            (s) =>
              !isUnavailableColumnAvailability(s?.node?.availability) ||
              (s?.node?.block?.id &&
                s?.node?.block?.blockSeries.isVisibleToMembers) ||
              s?.node?.bookings?.edges?.find((item) => item?.node?.id) ||
              (s?.node?.eventSlot?.id &&
                s?.node?.eventSlot?.eventSession?.id) ||
              s?.node?.hasSplitBooking
          ) || slotsGoingForward?.slice(-1).pop();
        const minsToNextSlot =
          nextSlotToRender?.node?.dateTime && cur?.node?.dateTime
            ? differenceInMinutes(
                convertAgendaDateTimeToDate(nextSlotToRender.node.dateTime),
                convertAgendaDateTimeToDate(cur.node.dateTime)
              )
            : nextSlotToRender?.node?.time && cur?.node?.time
            ? differenceInMinutes(
                parse(nextSlotToRender.node.time, "HH:mm:ss", new Date()),
                parse(cur.node.time, "HH:mm:ss", new Date())
              )
            : 0;
        const unavailableComponent = (
          <>
            {needTopUnavailableComponent && newEndUnavailableComponent ? (
              <UnavailableSlot
                durationMins={
                  minsToNextSlot + topUnavailableMinutes + endUnavailableMinutes
                }
                startTime={formatAgendaDateTimeHHMM(agendaFirstTime ?? "")}
              />
            ) : needTopUnavailableComponent ? (
              <UnavailableSlot
                durationMins={minsToNextSlot + topUnavailableMinutes}
                startTime={formatAgendaDateTimeHHMM(agendaFirstTime ?? "")}
              />
            ) : newEndUnavailableComponent ? (
              <UnavailableSlot
                durationMins={minsToNextSlot + endUnavailableMinutes}
                startTime={cur?.node?.time ? formatHHMM(cur?.node.time) : ""}
              />
            ) : (
              <UnavailableSlot
                durationMins={minsToNextSlot}
                startTime={cur?.node?.time ? formatHHMM(cur?.node.time) : ""}
              />
            )}
          </>
        );

        return {
          nextSlotToRender,
          slots: [...acc.slots, unavailableComponent],
        };
      }

      if (cur?.node && slotHasBlockingRequestRestrictions) {
        const slotsGoingForward = timeSlotEdges.slice(index + 1);
        const nextSlotToRender =
          slotsGoingForward?.find(
            (s) =>
              !["UNAVAILABLE", "BLOCKED", "AVAILABLE_AFTER_START"].includes(
                s?.node?.availability || ""
              ) ||
              (s?.node?.block?.id &&
                s?.node?.block?.blockSeries.isVisibleToMembers) ||
              s?.node?.bookings?.edges?.find((item) => item?.node?.id) ||
              (s?.node?.eventSlot?.id &&
                s?.node?.eventSlot?.eventSession?.id) ||
              s?.node?.hasSplitBooking
          ) || slotsGoingForward?.slice(-1).pop();
        const minsToNextSlot =
          nextSlotToRender?.node?.dateTime && cur?.node?.dateTime
            ? differenceInMinutes(
                convertAgendaDateTimeToDate(nextSlotToRender.node.dateTime),
                convertAgendaDateTimeToDate(cur.node.dateTime)
              )
            : 0;
        const unavailableComponent = (
          <>
            {needTopUnavailableComponent && newEndUnavailableComponent ? (
              <UnavailableSlot
                durationMins={
                  minsToNextSlot + topUnavailableMinutes + endUnavailableMinutes
                }
                startTime={
                  formatAgendaDateTimeHHMM(agendaFirstTime ?? "") ?? ""
                }
              />
            ) : needTopUnavailableComponent ? (
              <UnavailableSlot
                durationMins={minsToNextSlot + topUnavailableMinutes}
                startTime={
                  formatAgendaDateTimeHHMM(agendaFirstTime ?? "") ?? ""
                }
              />
            ) : newEndUnavailableComponent ? (
              <UnavailableSlot
                durationMins={minsToNextSlot + endUnavailableMinutes}
                startTime={
                  cur?.node?.dateTime
                    ? formatAgendaDateTimeHHMM(cur.node.dateTime) ?? ""
                    : cur?.node?.time
                    ? formatHHMM(cur.node.time)
                    : ""
                }
              />
            ) : (
              <UnavailableSlot
                durationMins={minsToNextSlot}
                startTime={
                  cur?.node?.dateTime
                    ? formatAgendaDateTimeHHMM(cur.node.dateTime) ?? ""
                    : cur?.node?.time
                    ? formatHHMM(cur.node.time)
                    : ""
                }
              />
            )}
          </>
        );

        return {
          nextSlotToRender,
          slots: [...acc.slots, unavailableComponent],
        };
      }

      /**
       * SLOT HAS REQUEST RESTRICTION
       */
      if (cur?.node && isRequestSlot) {
        const slotsToLookAhead =
          DEFAULT_SLOT_DURATION_MINUTES / AGENDA_MINUTES_PER_SLOT - 1; // Subtract one because we are the first slot

        const slotsToCheck = timeSlotEdges.slice(
          index + 1,
          index + 1 + slotsToLookAhead
        );

        // The next slot to render is the next one with something in it
        // OR if none in the next 30 minutes have something happening, it's the slot
        // 30 minutes away
        const nextSlotWithBlocker = slotsToCheck?.find((s) => {
          return (
            s?.node?.eventSlot?.id ||
            s?.node?.bookings?.edges?.find((item) => item?.node?.id)?.node
              ?.id ||
            s?.node?.hasSplitBooking
          );
        });

        // If we are starting on :15 or :45, we only render 1 15 minute slot
        // This is so we end up back at a every 30 minute cadence afterward
        const { minutes } = getHoursMinutesFromHHMM(cur.node.time);
        const isOffThe30Mark =
          minutes !== 0 && minutes !== DEFAULT_SLOT_DURATION_MINUTES;
        const nextSlotToFindIndex = isOffThe30Mark
          ? index + slotsToLookAhead
          : index + slotsToLookAhead + 1;
        const nextSlotByTime = timeSlotEdges[nextSlotToFindIndex] ?? null;
        const nextSlotToRender = nextSlotWithBlocker || nextSlotByTime;

        let minsToNextSlot =
          nextSlotToRender?.node?.dateTime && cur.node.dateTime
            ? differenceInMinutes(
                convertAgendaDateTimeToDate(nextSlotToRender.node.dateTime),
                convertAgendaDateTimeToDate(cur.node.dateTime)
              )
            : 0;

        // if last slot of day default to smallest slot duration
        const durationMins =
          minsToNextSlot === 0 ? MIN_SLOT_DURATION_MINUTES : minsToNextSlot;

        const requestComponent = (
          <>
            {needTopUnavailableComponent ? (
              <UnavailableSlot durationMins={topUnavailableMinutes} />
            ) : null}
            <RequestRestrictionSlot
              startTime={cur.node.time}
              durationMins={durationMins}
              queueSlotId={queueSlotId ?? ""}
            />
            {newEndUnavailableComponent ? (
              <UnavailableSlot durationMins={endUnavailableMinutes} />
            ) : null}
          </>
        );

        return { nextSlotToRender, slots: [...acc.slots, requestComponent] };
      }

      // Handle Booking Restrictions
      const currentSlotBookingRestrictions =
        cur?.node?.bookingRestrictions?.edges || [];

      const slotHasHiddenBookingRestrictions =
        currentSlotBookingRestrictions.length &&
        currentSlotBookingRestrictions.every(
          (e) => e?.node?.visibility === "SLOTS_ONLY"
        );

      // Slot has hidden booking restrictions which will be displayed as visible slots which are not clickable
      // If the slot has visible booking restrictions they will fall below and be rendered as unavailable
      // NB: This is using the same logic as below to render the "empty slots" the only difference is that it
      // sets the empty slot to disabled. This could probably be updated to combine the two but I thought keeping
      // them separate for now would make the requirements clearer.
      if (
        cur?.node &&
        (slotHasHiddenBookingRestrictions || slotHasHiddenRequestRestrictions)
      ) {
        const slotsToLookAhead =
          DEFAULT_SLOT_DURATION_MINUTES / AGENDA_MINUTES_PER_SLOT - 1; //Subtract one because we are the first slot
        const slotsToCheck = timeSlotEdges.slice(
          index + 1,
          index + 1 + slotsToLookAhead
        );

        const nextSlotWithBlocker = slotsToCheck?.find((s) => {
          return (
            s?.node?.block?.id ||
            s?.node?.eventSlot?.id ||
            s?.node?.bookings?.edges?.find((e) => e?.node?.id) ||
            s?.node?.hasSplitBooking
          );
        });

        // If we are starting on :15 or :45, we only render 1 15 minute slot
        // This is so we end up back at a every 30 minute cadence afterward
        const { minutes } = getHoursMinutesFromHHMM(cur.node.time);
        const isOffThe30Mark =
          minutes !== 0 && minutes !== DEFAULT_SLOT_DURATION_MINUTES;
        const nextSlotToFindIndex = isOffThe30Mark
          ? index + slotsToLookAhead
          : index + slotsToLookAhead + 1;
        const nextSlotByTime = timeSlotEdges[nextSlotToFindIndex] ?? null;
        const nextSlotToRender = nextSlotWithBlocker || nextSlotByTime;

        // Add the right component next
        const minsToNextSlot =
          nextSlotToRender?.node?.dateTime && cur.node.dateTime
            ? differenceInMinutes(
                convertAgendaDateTimeToDate(nextSlotToRender.node.dateTime),
                convertAgendaDateTimeToDate(cur.node.dateTime)
              )
            : DEFAULT_SLOT_DURATION_MINUTES;
        const EmptySlotComponent = (
          <>
            {needTopUnavailableComponent ? (
              <UnavailableSlot durationMins={topUnavailableMinutes} />
            ) : null}
            <EmptySlot
              key={cur.node.id}
              durationMins={minsToNextSlot}
              slotId={cur?.node.id}
              courseName={courseName}
              time={cur?.node.time}
              startDateTime={cur?.node.dateTime}
              {...{
                handleMultiSlotRowPress,
                setBookingWizardSlotId,
                setBookingWizardStartTime,
              }}
            />
            {newEndUnavailableComponent ? (
              <UnavailableSlot durationMins={endUnavailableMinutes} />
            ) : null}
          </>
        );
        return { nextSlotToRender, slots: [...acc.slots, EmptySlotComponent] };
      }

      /**
       * Slot is completely empty, and it's available.
       */
      if (cur?.node) {
        // Handle AVAILABLE_AFTER_START We need to see if any
        // of the next 5 slots are available after start this
        // means there is a restriction for when bookings can
        // start
        const maxSlotsToCheck = timeSlotEdges.slice(index + 1, index + 5);

        const hasAvailableAfterStart = maxSlotsToCheck?.find(
          (s) => s?.node?.availability === "AVAILABLE_AFTER_START"
        );

        const nextSlotWithBlockerOrAvailability = hasAvailableAfterStart
          ? maxSlotsToCheck?.find((s) => {
              return (
                s?.node?.availability !== "AVAILABLE_AFTER_START" ||
                s?.node?.block?.id ||
                s?.node?.eventSlot?.id ||
                isRequestSlot ||
                s?.node?.bookings?.edges?.find((e) => e?.node?.id) ||
                s?.node?.hasSplitBooking
              );
            })
          : null;

        // If we are not occupied, and we have a valid time, lets see if we are clear to render
        // 30 minutes out.
        const minSlotsToLookAhead =
          DEFAULT_SLOT_DURATION_MINUTES / AGENDA_MINUTES_PER_SLOT - 1; // Subtract one because we are the first slot

        const slotsToCheck = timeSlotEdges.slice(
          index + 1,
          index + minSlotsToLookAhead
        );

        const nextSlotWithBlocker = slotsToCheck?.find((s) => {
          return (
            s?.node?.availability !== "AVAILABLE" ||
            s?.node?.block?.id ||
            s?.node?.eventSlot?.id ||
            isRequestSlot ||
            s?.node?.bookings?.edges?.find((e) => e?.node?.id) ||
            s?.node?.hasSplitBooking
          );
        });

        // If we are starting on :15 or :45, we only render 1 15 minute slot
        // This is so we end up back at a every 30 minute cadence afterward
        const { minutes } = getHoursMinutesFromHHMM(cur.node.time);

        const isOffThe30Mark =
          minutes !== 0 && minutes !== DEFAULT_SLOT_DURATION_MINUTES;
        const nextSlotToFindIndex = isOffThe30Mark
          ? index + minSlotsToLookAhead
          : index + minSlotsToLookAhead + 1;

        const nextSlotByTime = timeSlotEdges[nextSlotToFindIndex] ?? null;

        const nextSlotToRender =
          nextSlotWithBlockerOrAvailability ||
          nextSlotWithBlocker ||
          nextSlotByTime;

        // Add the right component next
        const minsToNextSlot =
          nextSlotToRender?.node?.dateTime && cur.node.dateTime
            ? differenceInMinutes(
                convertAgendaDateTimeToDate(nextSlotToRender.node.dateTime),
                convertAgendaDateTimeToDate(cur.node.dateTime)
              )
            : MIN_SLOT_DURATION_MINUTES;

        const EmptySlotComponent = (
          <>
            {needTopUnavailableComponent ? (
              <UnavailableSlot durationMins={topUnavailableMinutes} />
            ) : null}
            <EmptySlot
              key={cur.node.id}
              durationMins={minsToNextSlot}
              slotId={cur?.node.id}
              isAvailable={canMemberBook}
              courseName={courseName}
              time={cur?.node.time}
              startDateTime={cur?.node.dateTime}
              {...{
                handleMultiSlotRowPress,
                setBookingWizardSlotId,
                setBookingWizardStartTime,
              }}
            />
            {newEndUnavailableComponent ? (
              <UnavailableSlot durationMins={endUnavailableMinutes} />
            ) : null}
          </>
        );
        return { nextSlotToRender, slots: [...acc.slots, EmptySlotComponent] };
      }

      /**
       * We should never get here, but this will avoid bad data crashing things, we'll just get
       * an empty array
       */
      return acc;
    },
    { nextSlotToRender: timeSlotEdges[0], slots: [] }
  );

  return (
    <VStack
      minWidth={COLUMN_MIN_WIDTH_PX}
      width={COLUMN_MIN_WIDTH_PX}
      maxWidth={{ base: COLUMN_MIN_WIDTH_PX, xs: `100%` }}
      flex={1}
    >
      <>
        <Box
          position={"absolute"}
          top={0}
          bottom={0}
          left={0}
          right={0}
          overflow={"hidden"}
        >
          {Array.from({ length: 48 }, (_, index) => index).map((value) => {
            return (
              <Box
                key={value}
                height={72}
                width={"100%"}
                borderTopWidth={1}
                borderTopColor={"surface-dividers"}
              />
            );
          })}
        </Box>
        {slots?.slots.map((slot, index) => {
          return (
            <Box width="100%" key={index}>
              {slot}
            </Box>
          );
        })}
      </>
    </VStack>
  );
};

export const SingleCourseColumnFragmentString = graphql`
  fragment SingleCourseColumn_course on Course {
    id
    name
    facility {
      id
      ...getFacilityMemberPermissions_facility
      club {
        supportsPublic
      }
    }
    timeSlots(first: 10000, dateGte: $date, dateLte: $date) {
      edges {
        node {
          id
          availability
          hasSplitBooking
          time
          dateTime
          bookings {
            edges {
              node {
                ...BookingSlot_booking
                id
                startSlot {
                  id
                }
                slots {
                  count
                  edges {
                    node {
                      id
                    }
                  }
                }
                ... on StandardBooking {
                  bufferSlots {
                    count
                  }
                }

                ... on LessonBooking {
                  bufferSlots {
                    count
                  }
                }
              }
            }
          }
          block {
            id
            blockSeries {
              isVisibleToMembers
            }
            ...BlockSlot_block
          }
          eventSlot {
            type
            id
            eventSession {
              id
              bufferMinsAfter
              bufferMinsBefore
              ...EventSlot_eventSession
            }
          }
          bookingRestrictions: bookingRules(
            type: [RESTRICTION]
            isBlocking: true
            bookingType: [STANDARD]
          ) {
            edges {
              liftAt
              node {
                id
                name
                ... on BookingRestrictionRule {
                  visibility: memberVisibility
                }
              }
            }
          }
          requestRestrictions: bookingRules(type: [REQUEST])
            @skip(if: $requestConfigs) {
            edges {
              liftAt
              node {
                id
              }
            }
          }
          configuredRequestRestrictions: requestWindowBookingRules(
            type: [REQUEST]
          ) @include(if: $requestConfigs) {
            edges {
              liftAt
              requestOpens
              requestCloses
              node {
                id
                ... on RequestRule {
                  memberVisibility
                  requestConfig {
                    id
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
